import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { ReportingDimension } from '../../types/reporting';
import { formatDisplayMoney, formatReportingAmount } from '../../lib/reportingMoney';
import type { FxRateSnapshot } from '../../types/fxRates';

/**
 * How money should render on this surface. Optional everywhere it is threaded —
 * an absent value means NATIVE, so every existing caller keeps its behaviour and
 * only the pages that opt in convert.
 */
export interface MoneyDisplay {
  display: string;
  snapshot: FxRateSnapshot | null;
}
import { classifyCampaignType, type ReportingTypeCategory } from '../../lib/reportingCampaignTypes';
import { metaAdSetUrl, metaAdUrl, metaCampaignUrl } from '../../lib/connectionLinks';
import {
  attributeVisibleOn,
  budgetUnitTag,
  nameStatus,
  statCount,
  statKind,
  statMoney,
  statPct,
  statusRole,
  type BreakdownColumn,
  type BreakdownStatKey,
  type StatusRole,
} from './breakdownColumns';
import type { BreakdownRowView } from './breakdownGrouping';
import { AdCreativeThumbnail } from './AdCreativeThumbnail';

const DASH = <span className="text-slate-300">—</span>;
const int = (n: number): string => Math.round(n).toLocaleString('en-US');

// External deep-link into Meta Ads Manager. New tab, no referrer/opener leak.
function ExtLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
      {children}
    </a>
  );
}

// The dimension cell for a rollup row: the node's name, linked to the right place
// for its dim, PREFIXED with the coloured delivery-status dot at its native level
// (campaign/ad-set/ad — nameStatus; blank on account rows + rollup subtotals).
// Account → our account detail page; Campaign/Ad set/Ad → the Meta Ads Manager
// deep link by platform id (plain text when a link can't be built — no dead
// links). Only ever called for the row's OWN dim (deepest shown value).
export function renderBreakdownDimCell(row: BreakdownRowView): ReactNode {
  const label = row.name ?? row.id;
  if (!label) return DASH;

  if (row.dim === 'account') {
    // Account has no delivery status → no dot. Unattributed (no id) → plain text.
    return row.id ? (
      <Link to={`/accounts/${row.id}`} className="text-blue-600 hover:underline">
        {label}
      </Link>
    ) : (
      <span>{label}</span>
    );
  }

  const url =
    row.dim === 'campaign'
      ? metaCampaignUrl(row.adAccountId, row.id)
      : row.dim === 'adset'
        ? metaAdSetUrl(row.adAccountId, row.id)
        : metaAdUrl(row.adAccountId, row.id);
  const nameEl = url ? <ExtLink href={url}>{label}</ExtLink> : <span>{label}</span>;

  // Each name cell shows ITS OWN level's status dot (renderBreakdownDimCell is
  // only ever called for the row's own dim, in both Flat + Tree).
  const status = nameStatus(row, row.dim);
  // Ad rows carry a creative thumbnail (Phase 3c) — a small lazy tile ahead of the
  // status dot + name, click to enlarge. Only on ad-level rows (they're a single
  // creative); campaign/ad-set/account rows never render one.
  const thumb = row.dim === 'ad' ? <AdCreativeThumbnail creative={row.creative} name={label} /> : null;

  if (!status && !thumb) return nameEl;
  return (
    <span className="inline-flex items-center gap-1.5">
      {thumb}
      {status && <StatusDot status={status} />}
      {nameEl}
    </span>
  );
}

// Plain (unlinked) label for a shallower dim column — never used today (only the
// row's own dim column is filled), but kept for callers that want the raw name.
export function dimLabel(dim: ReportingDimension, name: string | null, id: string | null): ReactNode {
  return name ?? id ?? DASH;
}

// One stat cell of a rollup row.
//
// ⚠️ Money goes through `formatDisplayMoney`: in NATIVE mode (the default, and
// what an omitted `display` means) that is byte-for-byte the old per-currency
// rendering; with an agency display currency it is the converted, blended figure.
// Ratios recompute from the summed metric block; counts are integers.
export function renderBreakdownStatCell(
  row: BreakdownRowView,
  key: BreakdownStatKey,
  money?: MoneyDisplay,
): ReactNode {
  const m = row.metrics;
  const kind = statKind(key);
  if (kind === 'count') return int(statCount(m, key));
  if (kind === 'pct') {
    const p = statPct(m, key);
    return p === null ? DASH : `${p.toFixed(2)}%`;
  }
  const amounts = statMoney(m, key);
  if (amounts.length === 0) return DASH;
  return formatDisplayMoney(amounts, money?.display ?? '', money?.snapshot ?? null);
}

// ---- attribute cells (dot / chip / compact value) --------------------------
//
// Two colour vocabularies, per the approved visual treatment: SEMANTIC for status
// (green active / grey paused / amber review / red rejected) and CATEGORICAL for
// type (teal / blue / purple ramps — never green/red, so they don't collide).

const STATUS_DOT_CLASS: Record<StatusRole, string> = {
  active: 'bg-emerald-500',
  paused: 'bg-slate-300',
  review: 'bg-amber-500',
  rejected: 'bg-red-500',
  unknown: 'bg-slate-300',
};

// The four legend keys (active is the quiet common case; amber/red catch the eye).
export const STATUS_LEGEND: { role: StatusRole; label: string }[] = [
  { role: 'active', label: 'active' },
  { role: 'paused', label: 'paused' },
  { role: 'review', label: 'in review / limited' },
  { role: 'rejected', label: 'rejected' },
];

// A bare 9px status dot, full word on hover (title attr).
function StatusDot({ status }: { status: string | null }) {
  const role = statusRole(status);
  return (
    <span
      className={`inline-block h-[9px] w-[9px] rounded-full ${STATUS_DOT_CLASS[role]}`}
      title={status ?? 'unknown'}
      aria-label={status ?? 'unknown'}
    />
  );
}

const TYPE_CHIP_CLASS: Record<ReportingTypeCategory, string> = {
  lead: 'bg-teal-50 text-teal-700',
  traffic: 'bg-blue-50 text-blue-700',
  engagement: 'bg-purple-50 text-purple-700',
  awareness: 'bg-purple-50 text-purple-700',
  other: 'bg-slate-100 text-slate-600',
};

// A small tinted category chip (short 1-2 word label).
function TypeChip({ objective, optimizationGoal }: { objective: string | null; optimizationGoal: string | null }) {
  const { label, category } = classifyCampaignType(objective, optimizationGoal);
  return (
    <span className={`inline-block rounded-full px-2 py-[2px] text-xs font-medium ${TYPE_CHIP_CLASS[category]}`}>
      {label}
    </span>
  );
}

// Compact budget: amount in primary text, the /day or /life unit muted. Money is
// per-currency (the row's native currency).
function BudgetValue({ amount, budgetType, currency }: { amount: number | null; budgetType: string | null; currency: string }) {
  if (amount == null) return DASH;
  return (
    <span className="whitespace-nowrap">
      <span className="text-slate-800">{formatReportingAmount(amount, currency)}</span>
      <span className="ml-0.5 text-slate-400">{budgetUnitTag(budgetType)}</span>
    </span>
  );
}

// Render an attribute cell — blank unless the column is visible on this row's
// native level (attributeVisibleOn), else the dot / chip / compact value.
export function renderBreakdownAttributeCell(row: BreakdownRowView, column: BreakdownColumn): ReactNode {
  if (!attributeVisibleOn(row, column)) return '';
  const attrs = row.attributes!;
  switch (column.attrType) {
    case 'objective':
      return <TypeChip objective={attrs.objective} optimizationGoal={attrs.optimizationGoal} />;
    case 'budget':
      return <BudgetValue amount={attrs.budgetAmount} budgetType={attrs.budgetType} currency={attrs.currency} />;
    default:
      return '';
  }
}

// The one-line status legend rendered under the table when a status column shows.
export function StatusDotLegend() {
  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
      <span className="uppercase tracking-wide text-slate-400">status</span>
      {STATUS_LEGEND.map(({ role, label }) => (
        <span key={role} className="inline-flex items-center gap-1.5">
          <span className={`inline-block h-[9px] w-[9px] rounded-full ${STATUS_DOT_CLASS[role]}`} aria-hidden />
          {label}
        </span>
      ))}
    </div>
  );
}
